import { useEffect, useRef, useState, type FormEvent } from 'react';
import Reveal from '@/components/base/Reveal';
import { stats, whatsappUrl } from '@/mocks/homeData';

const FORM_ADDR = 'https://readdy.ai/api/form/dan7hggp3fe23ggcgndg';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const businessTypes = [
  'Retail Pharmacy',
  'Healthcare Distributor',
  'Medical Store',
  'Wellness Center',
  'Hospital Partner',
  'Medicine Dealer',
  'Ayurveda Distributor',
  'Other',
];

const experienceOptions = [
  'Less than 1 year',
  '1 - 3 years',
  '3 - 5 years',
  '5 - 10 years',
  'More than 10 years',
];

const volumeOptions = [
  'Below ₹50,000',
  '₹50,000 - ₹1,00,000',
  '₹1,00,000 - ₹3,00,000',
  '₹3,00,000 - ₹5,00,000',
  'Above ₹5,00,000',
];

const applyPoints = [
  { id: 'territory', icon: 'ri-map-pin-range-line', text: 'Protected territory rights for your market' },
  { id: 'margin', icon: 'ri-percent-line', text: 'Attractive margins on 150+ herbal products' },
  { id: 'support', icon: 'ri-megaphone-line', text: 'Marketing & branding support from our team' },
  { id: 'supply', icon: 'ri-truck-line', text: 'Reliable, on-time pan-India supply' },
];

const isValidPhone = (value: string) => {
  const digits = value.replace(/[\s\-()]/g, '');
  const normalized = digits.startsWith('+91') ? digits.slice(3) : digits.replace(/^0/, '');
  return /^[6-9]\d{9}$/.test(normalized);
};

export default function DistributorFormSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [closing, setClosing] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status !== 'success') return undefined;
    setClosing(false);
    successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const hideTimer = window.setTimeout(() => setClosing(true), 3000);
    const clearTimer = window.setTimeout(() => {
      setStatus('idle');
      setClosing(false);
    }, 3600);
    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(clearTimer);
    };
  }, [status]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('company_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    formData.delete('company_alt');

    const phone = String(formData.get('phone') || '').trim();
    if (!isValidPhone(phone)) {
      setPhoneError('Please enter a valid 10-digit mobile number (e.g. +91 98421 83817).');
      return;
    }
    setPhoneError('');

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const okCode = parsed?.code === 'OK';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && okCode && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'We could not submit your application. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setFormError(error instanceof Error ? error.message : 'Network error. Please try again.');
    }
  };

  return (
    <section id="distributor-apply" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <Reveal>
          <div className="mb-10 md:mb-14 rounded-xl border border-background-200 bg-background-100 px-4 py-6 md:px-8 md:py-7">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {stats.map((item) => (
                <div key={item.id} className="text-center">
                  <p className="font-heading text-2xl md:text-3xl font-semibold text-primary-700">{item.value}</p>
                  <p className="mt-1 text-xs md:text-sm text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent-300 bg-accent-50 px-4 py-1.5 text-xs md:text-sm font-semibold text-accent-800">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-time-line text-sm"></i>
                </span>
                Avg. approval in 3 days
              </span>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left: intro + why apply */}
          <Reveal>
            <div className="w-full">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary-700">
                <span className="w-6 h-px bg-primary-700"></span>
                Distributor Application
              </span>
              <h2 className="font-heading mt-3 text-2xl md:text-4xl font-semibold leading-tight text-foreground-950">
                Apply to Become a Distributor
              </h2>
              <span className="block h-[3px] w-16 rounded-full bg-primary-500 mt-4"></span>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 max-w-xl">
                Ready to partner with Best Research Pharma? Fill in your details and our distribution team will
                review your application and reach out within two business days.
              </p>

              <ul className="mt-7 space-y-4">
                {applyPoints.map((item) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-base`}></i>
                    </span>
                    <span className="mt-1 text-sm md:text-base text-foreground-700">{item.text}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 p-5 rounded-lg bg-secondary-50 border border-secondary-200 flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                  <i className="ri-customer-service-2-line text-lg"></i>
                </span>
                <div className="flex-1">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">Prefer to talk first?</h3>
                  <p className="mt-0.5 text-xs md:text-sm text-foreground-600">
                    Chat with our team directly on WhatsApp for instant answers.
                  </p>
                </div>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  WhatsApp Us
                </a>
              </div>
            </div>
          </Reveal>

          {/* Right: application form */}
          <Reveal delay={120}>
            <div className="w-full rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
              <h3 className="font-heading text-xl font-semibold text-foreground-950">Distributor Application</h3>
              <p className="mt-2 text-sm text-foreground-600">
                All fields marked required help us evaluate your application faster.
              </p>

              <form
                data-readdy-form
                id="distributor-application-form"
                onSubmit={handleSubmit}
                className="mt-6 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="d-name" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Full Name
                    </label>
                    <input
                      id="d-name"
                      name="name"
                      type="text"
                      required
                      placeholder="Your name"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="d-business" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Business / Firm Name
                    </label>
                    <input
                      id="d-business"
                      name="business_name"
                      type="text"
                      required
                      placeholder="Your business name"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="d-phone" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Phone Number
                    </label>
                    <input
                      id="d-phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      required
                      placeholder="+91 XXXXX XXXXX"
                      onChange={() => {
                        if (phoneError) setPhoneError('');
                      }}
                      className={`w-full px-3.5 py-2.5 rounded-md border bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:border-primary-400 ${
                        phoneError
                          ? 'border-accent-400 focus:ring-accent-300'
                          : 'border-background-300 focus:ring-primary-400'
                      }`}
                    />
                    {phoneError ? (
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-accent-700">
                        <i className="ri-error-warning-line"></i>
                        {phoneError}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <label htmlFor="d-email" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Email Address
                    </label>
                    <input
                      id="d-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="d-territory" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      City / State
                    </label>
                    <input
                      id="d-territory"
                      name="territory"
                      type="text"
                      required
                      placeholder="e.g. Madurai, Tamil Nadu"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="d-type" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Business Type
                    </label>
                    <select
                      id="d-type"
                      name="business_type"
                      defaultValue=""
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="">Select business type</option>
                      {businessTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="d-experience" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Years in Business
                    </label>
                    <select
                      id="d-experience"
                      name="experience"
                      defaultValue=""
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="">Select experience</option>
                      {experienceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="d-volume" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Expected Monthly Volume
                    </label>
                    <select
                      id="d-volume"
                      name="monthly_volume"
                      defaultValue=""
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="">Select monthly volume</option>
                      {volumeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="d-message" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Tell Us About Your Requirement
                  </label>
                  <textarea
                    id="d-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    required
                    placeholder="Your market, current product lines, expected monthly volume, etc. (max 500 characters)"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                  ></textarea>
                </div>

                {/* Anti-spam field */}
                <input
                  className="form-hp-field"
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-base"></i>
                      Submit Application
                    </>
                  )}
                </button>

                {status === 'success' ? (
                  <div
                    ref={successRef}
                    className={`flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3 ${
                      closing ? 'fade-out-up' : 'animate-fade-up'
                    }`}
                  >
                    <i className="ri-checkbox-circle-fill mt-0.5"></i>
                    <span className="flex flex-col gap-0.5">
                      <span>Thank you! Your distributor application has been received.</span>
                      <span className="text-xs text-primary-600">
                        We&apos;ll call you within 2 business days to discuss the next steps.
                      </span>
                    </span>
                  </div>
                ) : null}

                {status === 'error' ? (
                  <p className="flex items-start gap-2 text-sm text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-4 py-3">
                    <i className="ri-error-warning-fill mt-0.5"></i>
                    {formError}
                  </p>
                ) : null}
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}